import { observer } from 'mobx-react-lite'

import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import type { Theme } from '@mui/system'

import { H3, Icon, P, Text } from '@mxenabled/mxui'

import { useGlobalCopyStore, useGlobalUiStore, useGoalStore, useScreenSize } from '../../common'
import { TrackType } from '../../core'

import { GoalStatusCard } from './GoalStatusCard'
import { ManageGoals } from './ManageGoals'
import { getZeroStateOngoing } from '../../core/utils/GoalsUtil'
import { formatCurrency } from '../../core/utils/NumberFormatting'

interface GoalListProps {
  trackType: TrackType
  onAddGoals: () => void
}

export const GoalList = observer(({ trackType, onAddGoals }: GoalListProps) => {
  const { isMobile } = useScreenSize()
  const { goals: copy } = useGlobalCopyStore()
  const { selectedAccountGuids } = useGlobalUiStore()
  const { orderedGoals: allGoals, setSelectedGoal } = useGoalStore()

  const goals = allGoals.filter(
    (goal) =>
      goal.track_type === trackType &&
      ((goal.track_type !== TrackType.RETIREMENT_TRACK &&
        selectedAccountGuids.includes(goal.account_guid)) ||
        goal.track_type === TrackType.RETIREMENT_TRACK),
  )

  const ongoing = goals.filter(
    (goal) =>
      (goal.track_type === TrackType.SAVINGS_TRACK && !goal.has_been_spent) ||
      (goal.track_type !== TrackType.SAVINGS_TRACK && !goal.is_complete),
  )

  const complete = goals.filter(
    (goal) =>
      (goal.track_type === TrackType.SAVINGS_TRACK && goal.has_been_spent) ||
      (goal.track_type !== TrackType.SAVINGS_TRACK && goal.is_complete),
  )

  const zeroStateComplete =
    trackType === TrackType.DEBT_TRACK
      ? copy.zero_state_complete_debt
      : copy.zero_state_complete_savings

  return (
    <Stack mt={16} mx={isMobile ? 24 : 0}>
      <Stack alignItems="center" direction="row" justifyContent="space-between" mb={8}>
        <H3 color="text.secondary" variant="body2">
          {copy.ongoing_title}
        </H3>
        <ManageGoals trackType={trackType} />
      </Stack>
      {ongoing.length > 0 ? (
        <Grid container={true} spacing={24}>
          {ongoing.map((goal, index, goals) => (
            // Goal should be full width in mobile or if it's the first goal (unless there are 2)
            <Grid key={index} size={isMobile || (index === 0 && goals.length !== 2) ? 12 : 6}>
              <GoalStatusCard goal={goal} key={goal.guid} onClick={() => setSelectedGoal(goal)} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Stack
          alignItems="center"
          bgcolor="background.default"
          borderRadius={2}
          height={166}
          justifyContent="center"
          py={18}
        >
          <P
            sx={(theme) => ({
              color: 'text.secondary',
              [(theme as Theme).breakpoints.down('sm')]: {
                px: 10,
              },
              width: { sm: 381 },
            })}
            variant="body2"
          >
            {getZeroStateOngoing(trackType, copy)}
          </P>
          <Button onClick={onAddGoals} sx={{ mt: 16, width: { sm: 224 } }} variant="contained">
            {copy.add_goals_button}
          </Button>
        </Stack>
      )}
      {trackType !== TrackType.RETIREMENT_TRACK && (
        <Stack gap={16} mt={48}>
          <H3 color="text.secondary" variant="body2">
            {copy.complete_title}
          </H3>
          {complete.length === 0 && (
            <Stack alignItems="center" bgcolor="background.default" borderRadius={2} py={18}>
              <P
                sx={(theme) => ({
                  color: 'text.secondary',
                  width: { sm: 393 },
                  [(theme as Theme).breakpoints.down('sm')]: {
                    px: 10,
                  },
                })}
                variant="body2"
              >
                {zeroStateComplete}
              </P>
            </Stack>
          )}
          {complete.map((goal) => (
            <Card elevation={2} key={goal.guid}>
              <CardActionArea onClick={() => setSelectedGoal(goal)}>
                <CardContent sx={{ ':last-child': { pb: 16 } }}>
                  <Stack alignItems="center" direction="row" justifyContent="space-between">
                    <Stack alignItems="center" direction="row" gap={6}>
                      <Icon color="success" fill={true} name="check_circle" size={20} />
                      <Text color="text.secondary" variant="body1">
                        {goal.name}
                      </Text>
                    </Stack>
                    <Text bold={true} variant="body1">
                      {formatCurrency(goal.amount, '0,0.00')}
                    </Text>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  )
})
